import { z } from "zod";
import { bookPresentationSchema } from "./demo.js";
import { bookPreviewStateSchema, bookRoleSchema, bookStateSchema, ulidSchema } from "./schemas.js";
import { contentModelSchema } from "./studio.js";
import { bookCreationBriefV1Schema } from "./book-creation-brief.js";
import { bookProductionProductSchema } from "./book-production-evidence.js";
import { studioEditableDocumentPlainText, studioEditableDocumentV2Schema, } from "./studio-editable-document.js";
export const studioExportFormatSchema = z.enum(["PDF", "EPUB"]);
export const studioExportStateSchema = z.enum(["QUEUED", "RENDERING", "READY", "NEEDS_ATTENTION"]);
export const studioPageLayoutSchema = z.enum([
    "ART_ABOVE",
    "ART_LEFT",
    "ART_RIGHT",
    "ART_INSET",
    "TEXT_FOCUS",
    "PHOTO_GRID",
    "RECIPE_FEATURE",
    "COMIC_GRID",
]);
export const studioTextAlignmentSchema = z.enum(["LEFT", "CENTER", "RIGHT"]);
export const studioBodyScaleSchema = z.enum(["COMPACT", "COMFORTABLE", "LARGE"]);
export const studioImageFitSchema = z.enum(["CONTAIN", "COVER"]);
export const studioBackgroundToneSchema = z.enum(["PAPER", "WARM", "SAGE", "MIDNIGHT"]);
export const studioFontPairSchema = z.enum([
    "HEIRLOOM_SERIF",
    "STORYBOOK",
    "EDITORIAL",
    "ACCESSIBLE",
]);
/** Someone in the storyteller's life, named as the family names them: "Harold", "my husband". */
export const studioPlanPersonV1Schema = z
    .object({
    name: z.string().trim().min(1).max(80),
    relationship: z.string().trim().max(60),
})
    .strict();
export const studioPlanContextV1Schema = z
    .object({
    version: z.literal(1),
    product: bookProductionProductSchema,
    title: z.string().trim().min(1).max(200),
    storyteller: z.string().trim().min(1).max(200),
    dedication: z.string().trim().max(5_000),
    /**
     * Who the book is for. Our Story may be a gift to the partner, or to someone else named in
     * `audienceName` (book quality batch M4): the book then says it is for them, never "the family".
     */
    audience: z.enum(["FAMILY", "CHILDREN", "WIDER_CIRCLE", "PARTNER", "SOMEONE"]),
    /** Who the book is for, by name, when that is someone else (audience SOMEONE). */
    audienceName: z.string().trim().min(1).max(80).optional(),
    /**
     * The name the closing letter is signed with, as its reader knows the storyteller: "Nonnie",
     * "Grandpa Joe" (M3). Without it, the storyteller's first name.
     */
    signedAs: z.string().trim().min(1).max(60).optional(),
    /** Our Story: the partner, by name, printed with the storyteller's on the cover. */
    partner: z.string().trim().min(1).max(120).optional(),
    /** Our Story: the year their story began, when they met or married ("Since 1971"). */
    since: z.number().int().min(1900).max(2100).optional(),
    /** The year and the place the storyteller was born, for the book's timeline. */
    bornYear: z.number().int().min(1880).max(2100).optional(),
    bornPlace: z.string().trim().min(1).max(80).optional(),
    /** The people in the story: the writer keeps their names right, and the book lists them. */
    people: z.array(studioPlanPersonV1Schema).max(24).optional(),
})
    .strict();
/**
 * From this studio response version on, the storyteller details carry a gift's recipient and the
 * letter's signature (book quality batch M3, M4). A client from before reads them strictly, so it
 * gets the details as it knows them (review, 2026-11-03).
 */
export const STUDIO_RESPONSE_GIFT_VERSION = 2;
/** Whether storyteller details hold anything only a client of the gift version can read. */
export function studioPlanContextNeedsGiftVersion(context) {
    return Boolean(context &&
        (context.audience === "PARTNER" ||
            context.audience === "SOMEONE" ||
            context.audienceName !== undefined ||
            context.signedAs !== undefined));
}
/** The storyteller details as a client of `responseVersion` reads them. */
export function studioPlanContextForVersion(context, responseVersion) {
    if (Number(responseVersion) >= STUDIO_RESPONSE_GIFT_VERSION)
        return context;
    const { audienceName: _name, signedAs: _signedAs, ...older } = context;
    void _name;
    void _signedAs;
    return {
        ...older,
        audience: older.audience === "PARTNER" || older.audience === "SOMEONE" ? "FAMILY" : older.audience,
    };
}
export const studioCreationBriefReadinessV1Schema = z
    .object({
    state: z.enum(["NOT_STARTED", "CURRENT", "STALE", "BLOCKED"]),
    product: bookProductionProductSchema.nullable(),
    reasonCode: z.enum([
        "NOT_COMPILED",
        "CURRENT_SUPPLIED_CONTEXT",
        "BOOK_REVISION_CHANGED",
        "RECIPE_EXACT_SOURCE_REQUIRED",
        "OUR_STORY_TWO_PRINCIPALS_REQUIRED",
    ]),
    briefRevision: z.number().int().positive().nullable(),
    answeredCount: z.number().int().nonnegative(),
    unknownCount: z.number().int().nonnegative(),
    excludedCount: z.number().int().nonnegative(),
})
    .strict();
export const studioPageDesignV1Schema = z
    .object({
    pageId: z.string().trim().min(1).max(160),
    contentItemId: z.string().trim().min(1).max(160).optional(),
    label: z.string().trim().min(1).max(120),
    layout: studioPageLayoutSchema,
    textAlignment: studioTextAlignmentSchema,
    bodyScale: studioBodyScaleSchema,
    imageFit: studioImageFitSchema,
    focalPoint: z.object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1) }).strict(),
    backgroundTone: studioBackgroundToneSchema,
    showPageNumber: z.boolean(),
    titleOverride: z.string().trim().max(200).optional(),
    textOverride: z.string().trim().max(5_000).optional(),
    editableDocument: studioEditableDocumentV2Schema.optional(),
    altText: z.string().trim().max(1_000).default(""),
})
    .strict()
    .superRefine((page, context) => {
    const editable = page.editableDocument;
    if (!editable)
        return;
    if (editable.surfaceId !== page.pageId)
        context.addIssue({
            code: "custom",
            path: ["editableDocument", "surfaceId"],
            message: "The editable document must identify this exact designed page.",
        });
    if (editable.contentItemId !== (page.contentItemId ?? null))
        context.addIssue({
            code: "custom",
            path: ["editableDocument", "contentItemId"],
            message: "The editable document must retain the page's exact content-item identity.",
        });
    if (page.textOverride === undefined)
        context.addIssue({
            code: "custom",
            path: ["textOverride"],
            message: "Semantic page editing must retain its renderer-compatible text projection.",
        });
    else if (studioEditableDocumentPlainText(editable) !== page.textOverride)
        context.addIssue({
            code: "custom",
            path: ["textOverride"],
            message: "The renderer-compatible text must exactly match the semantic document.",
        });
});
export const studioBookDesignV1Schema = z
    .object({
    version: z.literal(1),
    revision: z.number().int().nonnegative(),
    theme: z.enum(["HEIRLOOM", "EDITORIAL", "MINIMAL", "PLAYFUL"]),
    trim: z.enum(["5X8", "6X9", "8X10", "8_5X8_5"]),
    interiorColor: z.enum(["BLACK_AND_WHITE", "STANDARD_COLOR", "PREMIUM_COLOR"]),
    fontPair: studioFontPairSchema,
    readableFont: z.boolean(),
    highContrast: z.boolean(),
    pages: z.array(studioPageDesignV1Schema).max(200),
})
    .strict()
    .superRefine((design, context) => {
    const ids = design.pages.map((page) => page.pageId);
    if (new Set(ids).size !== ids.length)
        context.addIssue({
            code: "custom",
            path: ["pages"],
            message: "Page design identifiers must be unique.",
        });
});
export const studioExportSchema = z
    .object({
    exportId: ulidSchema,
    bookId: ulidSchema,
    sourceRevision: z.number().int().nonnegative(),
    format: studioExportFormatSchema,
    state: studioExportStateSchema,
    byteSize: z.number().int().positive().nullable(),
    checksumSha256: z
        .string()
        .regex(/^[a-f0-9]{64}$/i)
        .nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
})
    .strict();
export const studioPreviewSummarySchema = z
    .object({
    previewId: ulidSchema,
    sourceRevision: z.number().int().nonnegative(),
    state: bookPreviewStateSchema,
    updatedAt: z.string().datetime(),
})
    .strict();
export const bookStudioSchema = z
    .object({
    bookId: ulidSchema,
    revision: z.number().int().nonnegative(),
    presentation: bookPresentationSchema,
    design: studioBookDesignV1Schema,
    latestPreview: studioPreviewSummarySchema.nullable(),
    exports: z.array(studioExportSchema).max(50),
    includedPrintCopy: z
        .object({
        entitlementId: ulidSchema,
        eligible: z.literal(true),
        orderReadiness: z.enum([
            "AWAITING_APPROVED_PRINT_PACKAGE",
            "AWAITING_PROVIDER_ACTIVATION",
            "READY_FOR_DELIVERY",
        ]),
    })
        .strict()
        .optional(),
    creationBrief: bookCreationBriefV1Schema.nullable().optional(),
    creationBriefReadiness: studioCreationBriefReadinessV1Schema.optional(),
    /** The storyteller details as last saved, for the form that changes them. */
    creationPlanContext: studioPlanContextV1Schema.nullable().optional(),
})
    .strict();
export const updateBookStudioCommandSchema = z
    .object({
    presentation: bookPresentationSchema,
    design: studioBookDesignV1Schema.optional(),
    planContext: studioPlanContextV1Schema.optional(),
})
    .strict();
export const requestStudioExportCommandSchema = z
    .object({ format: studioExportFormatSchema })
    .strict();
export const bookStudioResponseSchema = z.object({ data: bookStudioSchema }).strict();
export const studioExportResponseSchema = z.object({ data: studioExportSchema }).strict();
const approvedReaderContentIdSchema = z.string().trim().min(1).max(160);
const approvedReaderSha256Schema = z.string().regex(/^[a-f0-9]{64}$/u);
export const approvedReaderContentStateSchema = z.enum(["READY", "EMPTY"]);
export const approvedReaderPageKindSchema = z.enum(["CHAPTER", "STORY_PAGE", "RECIPE"]);
export const approvedReaderProofStateSchema = z.enum([
    "UNAVAILABLE",
    "NOT_REQUESTED",
    "QUEUED",
    "RENDERING",
    "PREFLIGHTING",
    "READY",
    "PREFLIGHT_FAILED",
    "NEEDS_ATTENTION",
    "STALE",
    "APPROVED",
]);
export const approvedReaderBookMetadataSchema = z
    .object({
    id: ulidSchema,
    title: z.string().trim().min(1).max(160),
    storytellerName: z.string().trim().min(1).max(100),
    role: bookRoleSchema,
    state: bookStateSchema,
    revision: z.number().int().nonnegative(),
    templateName: z.string().trim().min(1).max(160),
    contentModel: contentModelSchema,
})
    .strict();
export const approvedReaderPresentationSchema = z
    .object({
    sourceRevision: z.number().int().nonnegative(),
    fingerprintSha256: approvedReaderSha256Schema,
    coverTitle: z.string().trim().min(1).max(160),
    subtitle: z.string().trim().max(240),
    byline: z.string().trim().min(1).max(120),
    orderedContentIds: z.array(approvedReaderContentIdSchema).min(1).max(200),
    displayTitles: z
        .record(approvedReaderContentIdSchema, z.string().trim().min(1).max(200))
        .refine((titles) => Object.keys(titles).length <= 200, {
        message: "Presentation display titles cannot exceed 200 entries.",
    }),
})
    .strict()
    .superRefine((presentation, context) => {
    if (new Set(presentation.orderedContentIds).size !== presentation.orderedContentIds.length)
        context.addIssue({
            code: "custom",
            path: ["orderedContentIds"],
            message: "Presentation content order must not contain duplicates.",
        });
    for (const contentId of Object.keys(presentation.displayTitles))
        if (!presentation.orderedContentIds.includes(contentId))
            context.addIssue({
                code: "custom",
                path: ["displayTitles", contentId],
                message: "Presentation display titles must refer to ordered content.",
            });
});
export const approvedReaderProofSchema = z
    .object({
    state: approvedReaderProofStateSchema,
    sourceRevision: z.number().int().nonnegative().optional(),
    currentRevision: z.boolean(),
    readyPdfCount: z.number().int().nonnegative().max(50),
})
    .strict();
export const MEMBER_APPROVED_READER_MAX_PAGES = 200;
export const MEMBER_APPROVED_READER_MAX_PARAGRAPHS_PER_PAGE = 200;
export const MEMBER_APPROVED_READER_MAX_PARAGRAPH_LENGTH = 10_000;
const memberApprovedReaderParagraphSchema = z
    .string()
    .min(1)
    .max(MEMBER_APPROVED_READER_MAX_PARAGRAPH_LENGTH)
    .refine((value) => value.trim().length > 0, {
    message: "Reader paragraphs must contain visible text.",
});
export const memberApprovedReaderPageSchema = z
    .object({
    id: approvedReaderContentIdSchema,
    kind: approvedReaderPageKindSchema,
    label: z.string().trim().min(1).max(120),
    title: z.string().trim().min(1).max(200),
    paragraphs: z
        .array(memberApprovedReaderParagraphSchema)
        .min(1)
        .max(MEMBER_APPROVED_READER_MAX_PARAGRAPHS_PER_PAGE),
    revision: z.number().int().positive(),
    immutable: z.literal(true),
    contentChecksum: approvedReaderSha256Schema,
    approvedVisualAttached: z.boolean(),
})
    .strict();
export const memberApprovedReaderBookSchema = z
    .object({
    version: z.literal(1),
    book: approvedReaderBookMetadataSchema,
    contentState: approvedReaderContentStateSchema,
    revisionSetId: approvedReaderSha256Schema,
    presentation: approvedReaderPresentationSchema.optional(),
    proof: approvedReaderProofSchema,
    pages: z.array(memberApprovedReaderPageSchema).max(MEMBER_APPROVED_READER_MAX_PAGES),
})
    .strict()
    .superRefine((content, context) => {
    if (content.contentState === "READY" && content.pages.length === 0)
        context.addIssue({
            code: "custom",
            path: ["pages"],
            message: "Ready reader content must contain at least one approved page.",
        });
    if (content.contentState === "EMPTY" && content.pages.length !== 0)
        context.addIssue({
            code: "custom",
            path: ["pages"],
            message: "Empty reader content cannot contain pages.",
        });
    const pageIds = content.pages.map((page) => page.id);
    if (new Set(pageIds).size !== pageIds.length)
        context.addIssue({
            code: "custom",
            path: ["pages"],
            message: "Approved reader page identifiers must be unique.",
        });
    if (!content.presentation)
        return;
    if (content.presentation.sourceRevision !== content.book.revision)
        context.addIssue({
            code: "custom",
            path: ["presentation", "sourceRevision"],
            message: "Reader presentation must match the current book revision.",
        });
    if (pageIds.length !== content.presentation.orderedContentIds.length ||
        pageIds.some((pageId) => !content.presentation?.orderedContentIds.includes(pageId)))
        context.addIssue({
            code: "custom",
            path: ["presentation", "orderedContentIds"],
            message: "Reader presentation must contain exactly the approved page identifiers.",
        });
});
export const memberApprovedReaderResponseSchema = z
    .object({ data: memberApprovedReaderBookSchema })
    .strict();
// Descriptive aliases retained for callers that name the versioned payload by its content role.
export const approvedReaderPageSchema = memberApprovedReaderPageSchema;
export const approvedReaderContentV1Schema = memberApprovedReaderBookSchema;
export const approvedReaderContentResponseSchema = memberApprovedReaderResponseSchema;
export const studioExportDownloadSchema = z
    .object({
    data: z
        .object({
        exportId: ulidSchema,
        format: z.literal("PDF"),
        fileName: z.string().regex(/^lifes-capsule-review-[A-Za-z0-9_-]+\.pdf$/u),
        downloadUrl: z.string().url(),
        expiresIn: z.number().int().positive().max(300),
        byteSize: z.number().int().positive(),
        pageCount: z.number().int().positive(),
        checksumSha256: z.string().regex(/^[a-f0-9]{64}$/iu),
    })
        .strict(),
})
    .strict();
//# sourceMappingURL=book-studio.js.map